import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';

const AudioContext = createContext();

// Use environment variable for stream URL
const STREAM_URL = process.env.REACT_APP_STREAM_URL || 'https://manager11.streamradio.fr:2485/stream';

// Default artwork for Media Session
const DEFAULT_ARTWORK = 'https://customer-assets.emergentagent.com/job_0f6d5a99-b561-454b-bc4e-ddaee0ea145c/artifacts/i142ayxr_LOGO%20MAJESTIC%20JUKEBOX%20RADIO%20OK.jpg';

// Default track state — single source of truth so all fallbacks stay consistent.
const DEFAULT_TRACK = {
  title: 'Live',
  artist: 'Click Play to listen',
  album: '',
  artwork: null
};

// localStorage key (slug-scoped so this PWA never clashes with another radio PWA)
const LAST_TRACK_STORAGE_KEY = 'radio_majesticjukeboxradio_lastTrack';

// ----- Artwork Universal API (1 brain for the whole network) -----
// Source of truth: central admin (validated covers) + iTunes scored, server-side.
// Returned artist/title are CLEANED of slogans / CamelCase by the backend, so we
// use them for the UI as well (player, Media Session, history, tab title).
// 3-level cascade:
//   1. /universal/resolve hits with artwork → use as-is, STOP (skip cascade).
//   2. /universal/resolve fails (timeout / 5xx / network) → local cascade
//      (iTunes / Deezer / MusicBrainz inside enhanced-metadata).
//   3. /universal/resolve responded but artwork is null AND level 2 failed
//      → fallback to the radio logo (handled naturally by DEFAULT_TRACK).
const UNIVERSAL_API_BASE =
  process.env.REACT_APP_UNIVERSAL_API ||
  'https://radio-app-emergent.emergent.host/api/universal';
const RADIO_NAME = 'MAJESTIC JUKEBOX RADIO';
const RADIO_ID = 'majestic';
const UNIVERSAL_TIMEOUT_MS = 4000;
const UNIVERSAL_TTL_MS = 60 * 1000; // 60s client cache per (artist|title)
const universalCache = new Map(); // key -> { at, value }

async function resolveUniversal(artist, title) {
  if (!artist || !title) return { status: 'invalid', data: null };
  const key = `${artist}|${title}`.toLowerCase();
  const cached = universalCache.get(key);
  if (cached && Date.now() - cached.at < UNIVERSAL_TTL_MS) {
    return cached.value;
  }
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), UNIVERSAL_TIMEOUT_MS);
    const url =
      `${UNIVERSAL_API_BASE}/resolve` +
      `?artist=${encodeURIComponent(artist)}` +
      `&title=${encodeURIComponent(title)}` +
      `&radio_name=${encodeURIComponent(RADIO_NAME)}` +
      `&radio_id=${encodeURIComponent(RADIO_ID)}`;
    const res = await fetch(url, { signal: controller.signal });
    clearTimeout(timer);
    if (!res.ok) {
      const value = { status: 'error', data: null };
      universalCache.set(key, { at: Date.now(), value });
      return value;
    }
    const data = await res.json();
    const value = { status: 'ok', data };
    universalCache.set(key, { at: Date.now(), value });
    return value;
  } catch (e) {
    // Timeout / abort / network — caller falls back to local cascade
    return { status: 'error', data: null };
  }
}

// On boot, restore the last successfully-resolved track (with artwork) so the
// pochette appears INSTANTLY instead of showing the bare logo for 1–2 sec.
function loadCachedTrack() {
  try {
    const raw = localStorage.getItem(LAST_TRACK_STORAGE_KEY);
    if (!raw) return DEFAULT_TRACK;
    const cached = JSON.parse(raw);
    if (cached && typeof cached === 'object' && cached.artwork) {
      return { ...DEFAULT_TRACK, ...cached };
    }
  } catch (e) {
    console.warn('[AudioContext] localStorage restore skipped:', e && e.message);
  }
  return DEFAULT_TRACK;
}

export const AudioProvider = ({ children }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [currentTrack, setCurrentTrack] = useState(loadCachedTrack);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const audioRef = useRef(null);
  const metadataIntervalRef = useRef(null);
  // Dedup: skip /enhanced-metadata when the stream tag hasn't changed.
  const lastStreamTrackRef = useRef('');

  // ---- Watchdog / auto-reconnect refs ----
  // User intent: true if user pressed Play (and didn't press Pause since).
  const wantPlayRef = useRef(false);
  // setInterval id for the watchdog
  const watchdogRef = useRef(null);
  // setTimeout id for the next backoff retry
  const reconnectTimerRef = useRef(null);
  // Number of consecutive failed reconnect attempts (for exponential backoff)
  const retryCountRef = useRef(0);
  // Last observed audio progress (to detect stalls)
  const lastProgressRef = useRef({ time: 0, at: 0 });
  // Re-entrancy guard for reconnect()
  const reconnectingRef = useRef(false);

  // Update Media Session metadata (for CarPlay, Lock Screen, etc.)
  const updateMediaSession = useCallback((track) => {
    if ('mediaSession' in navigator) {
      const artworkUrl = track.artwork || DEFAULT_ARTWORK;
      
      navigator.mediaSession.metadata = new MediaMetadata({
        title: track.title || 'Live Stream',
        artist: track.artist || 'MAJESTIC JUKEBOX RADIO',
        album: track.album || 'MAJESTIC JUKEBOX RADIO',
        artwork: [
          { src: artworkUrl, sizes: '96x96', type: 'image/jpeg' },
          { src: artworkUrl, sizes: '128x128', type: 'image/jpeg' },
          { src: artworkUrl, sizes: '192x192', type: 'image/jpeg' },
          { src: artworkUrl, sizes: '256x256', type: 'image/jpeg' },
          { src: artworkUrl, sizes: '384x384', type: 'image/jpeg' },
          { src: artworkUrl, sizes: '512x512', type: 'image/jpeg' },
        ]
      });

      // Set playback state
      navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
    }
  }, [isPlaying]);

  // Setup Media Session action handlers
  const setupMediaSessionHandlers = useCallback(() => {
    if ('mediaSession' in navigator) {
      navigator.mediaSession.setActionHandler('play', () => {
        play();
      });
      
      navigator.mediaSession.setActionHandler('pause', () => {
        pause();
      });
      
      navigator.mediaSession.setActionHandler('stop', () => {
        pause();
      });

      // For streaming, disable seek actions
      navigator.mediaSession.setActionHandler('seekbackward', null);
      navigator.mediaSession.setActionHandler('seekforward', null);
      navigator.mediaSession.setActionHandler('seekto', null);
      navigator.mediaSession.setActionHandler('previoustrack', null);
      navigator.mediaSession.setActionHandler('nexttrack', null);
    }
  }, []);

  // ---- Watchdog helpers ----
  // Append a cache-busting param so each reconnect actually re-opens the stream
  const buildStreamUrl = () => {
    try {
      const u = new URL(STREAM_URL);
      u.searchParams.set('_t', Date.now().toString());
      return u.toString();
    } catch (_) {
      const sep = STREAM_URL.includes('?') ? '&' : '?';
      return `${STREAM_URL}${sep}_t=${Date.now()}`;
    }
  };

  const clearReconnectTimer = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  const reconnect = async (reason) => {
    if (!wantPlayRef.current || !audioRef.current) return;
    if (reconnectingRef.current) return;
    reconnectingRef.current = true;
    console.log('[watchdog] reconnect:', reason, 'attempt', retryCountRef.current + 1);
    try {
      try { audioRef.current.pause(); } catch (_) { /* already detached */ }
      audioRef.current.src = buildStreamUrl();
      audioRef.current.load();
      await audioRef.current.play();
      retryCountRef.current = 0;
      lastProgressRef.current = { time: 0, at: Date.now() };
      setError(null);
      console.log('[watchdog] reconnect success');
    } catch (err) {
      retryCountRef.current = Math.min(retryCountRef.current + 1, 8);
      const delay = Math.min(30000, 1000 * Math.pow(2, retryCountRef.current - 1));
      console.warn('[watchdog] reconnect failed, retry in', delay, 'ms', err && err.message);
      clearReconnectTimer();
      reconnectTimerRef.current = setTimeout(() => reconnect('retry'), delay);
    } finally {
      reconnectingRef.current = false;
    }
  };

  const startWatchdog = () => {
    if (watchdogRef.current) return;
    lastProgressRef.current = {
      time: (audioRef.current && audioRef.current.currentTime) || 0,
      at: Date.now()
    };
    watchdogRef.current = setInterval(() => {
      if (!wantPlayRef.current || !audioRef.current) return;
      const a = audioRef.current;
      const now = Date.now();
      // If audio is paused while user wants it to play, recover
      if (a.paused) {
        reconnect('paused-while-want-play');
        return;
      }
      // Detect stall: currentTime hasn't advanced for >15s
      if (a.currentTime > lastProgressRef.current.time + 0.1) {
        lastProgressRef.current = { time: a.currentTime, at: now };
      } else if (now - lastProgressRef.current.at > 15000) {
        // freeze it so we don't fire multiple times
        lastProgressRef.current.at = now;
        reconnect('stalled-no-progress');
      }
    }, 5000);
  };

  const stopWatchdog = () => {
    if (watchdogRef.current) {
      clearInterval(watchdogRef.current);
      watchdogRef.current = null;
    }
    clearReconnectTimer();
    retryCountRef.current = 0;
    reconnectingRef.current = false;
  };

  // Initialize audio element
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.crossOrigin = 'anonymous';
      audioRef.current.volume = volume;
      audioRef.current.preload = 'metadata';
      
      // Important for iOS background playback
      audioRef.current.setAttribute('playsinline', 'true');
      audioRef.current.setAttribute('webkit-playsinline', 'true');
      
      // Add event listeners
      audioRef.current.addEventListener('error', (e) => {
        console.error('Audio error:', e);
        const errorMsg = audioRef.current && audioRef.current.error;
        if (errorMsg) {
          console.error('Error code:', errorMsg.code, 'message:', errorMsg.message);
        }
        // Auto-reconnect instead of giving up if the user wants to keep listening
        if (wantPlayRef.current) {
          reconnect('audio-error');
        } else if (audioRef.current && audioRef.current.src) {
          setError('Unable to load stream. Please check your connection.');
          setIsPlaying(false);
        }
      });
      
      audioRef.current.addEventListener('canplay', () => {
        console.log('Audio ready to play');
        setError(null);
      });
      
      audioRef.current.addEventListener('playing', () => {
        console.log('Audio is playing');
        setIsPlaying(true);
        setError(null);
        retryCountRef.current = 0;
        lastProgressRef.current = {
          time: audioRef.current ? audioRef.current.currentTime : 0,
          at: Date.now()
        };
        if ('mediaSession' in navigator) {
          navigator.mediaSession.playbackState = 'playing';
        }
      });
      
      audioRef.current.addEventListener('pause', () => {
        console.log('Audio paused');
        if ('mediaSession' in navigator) {
          navigator.mediaSession.playbackState = 'paused';
        }
        // Unexpected pause (network drop, iOS interruption, etc.) while user
        // still wants playback → try to recover after a short grace period.
        if (wantPlayRef.current) {
          setTimeout(() => {
            if (
              wantPlayRef.current &&
              audioRef.current &&
              audioRef.current.paused
            ) {
              reconnect('unexpected-pause');
            }
          }, 1500);
        }
      });
      
      audioRef.current.addEventListener('waiting', () => {
        console.log('Audio buffering...');
      });
      
      audioRef.current.addEventListener('stalled', () => {
        console.warn('Audio stalled');
        if (wantPlayRef.current) {
          // Give the network 5s to recover, then reconnect
          setTimeout(() => {
            if (
              wantPlayRef.current &&
              audioRef.current &&
              (audioRef.current.paused || audioRef.current.readyState < 2)
            ) {
              reconnect('stalled-event');
            }
          }, 5000);
        }
      });

      audioRef.current.addEventListener('ended', () => {
        console.warn('Audio ended (live stream should not end)');
        if (wantPlayRef.current) {
          reconnect('ended');
        }
      });

      audioRef.current.addEventListener('suspend', () => {
        console.log('Audio suspended');
      });

      audioRef.current.addEventListener('loadstart', () => {
        console.log('Audio loading started');
      });

      // Setup Media Session handlers
      setupMediaSessionHandlers();

      // Network + visibility recovery hooks
      const onOnline = () => {
        if (wantPlayRef.current && audioRef.current && audioRef.current.paused) {
          console.log('[watchdog] network back online — reconnecting');
          retryCountRef.current = 0;
          reconnect('online');
        }
      };
      const onVisibility = () => {
        if (
          document.visibilityState === 'visible' &&
          wantPlayRef.current &&
          audioRef.current &&
          audioRef.current.paused
        ) {
          console.log('[watchdog] page visible again — reconnecting');
          retryCountRef.current = 0;
          reconnect('visible');
        }
      };
      window.addEventListener('online', onOnline);
      document.addEventListener('visibilitychange', onVisibility);

      // Stash for cleanup
      audioRef.current._cleanupListeners = () => {
        window.removeEventListener('online', onOnline);
        document.removeEventListener('visibilitychange', onVisibility);
      };
    }

    return () => {
      stopWatchdog();
      if (audioRef.current) {
        if (typeof audioRef.current._cleanupListeners === 'function') {
          audioRef.current._cleanupListeners();
        }
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
      if (metadataIntervalRef.current) {
        clearInterval(metadataIntervalRef.current);
      }
    };
  }, [setupMediaSessionHandlers]);

  // Update Media Session when track changes
  useEffect(() => {
    updateMediaSession(currentTrack);
  }, [currentTrack, updateMediaSession]);

  // Determine the API base URL
  const getApiUrl = (endpoint) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    if (window.location.hostname.includes('netlify.app') || !backendUrl) {
      return `/.netlify/functions/${endpoint}`;
    }
    return `${backendUrl}/api/${endpoint}`;
  };

  // Fetch metadata from Icecast via Netlify Function or Backend API
  const fetchMetadata = async () => {
    try {
      const response = await axios.get(getApiUrl('stream-metadata'));

      if (response.data.success || response.data.artist) {
        const { artist, title } = response.data;
        const newArtist = artist || 'On Air';
        const newTitle = title || 'Live Stream';

        // Dedup — skip the heavier enhanced-metadata call if the stream
        // tag is identical to the previous tick.
        const trackKey = `${newArtist}|${newTitle}`.toLowerCase();
        const isSameTrack = trackKey === lastStreamTrackRef.current;
        lastStreamTrackRef.current = trackKey;

        if (isSameTrack) {
          return;
        }

        // Song changed — RESET artwork/album immediately so the LOGO shows
        // until the new artwork is fetched. We never keep the previous track's
        // pochette (avoids displaying the wrong cover during the gap).
        setCurrentTrack({
          ...DEFAULT_TRACK,
          title: newTitle,
          artist: newArtist
        });

        // If we have track and artist, try to get album art.
        if (newTitle && newArtist && newTitle !== 'On Air') {
          // Display values — may be overridden by Level 1 cleaned values from the central API.
          let displayArtist = newArtist;
          let displayTitle = newTitle;

          // ---- LEVEL 1: Universal API (admin-validated + scored iTunes) ----
          const u = await resolveUniversal(newArtist, newTitle);
          if (u.status === 'ok' && u.data) {
            const d = u.data;

            // ---- LEVEL 0: stale stream guard ----
            // When the central detects the Icecast tag hasn't changed for ~6 min
            // it returns { stale: true } → we MUST hide title/artist and show
            // the radio logo so the player never lies to listeners.
            const stale = !!d.stale;
            const cover = stale ? DEFAULT_ARTWORK : (d.artwork || null);
            const displayArtistRaw = stale ? '' : ((d.artist && d.artist.trim()) || newArtist);
            const displayTitleRaw = stale ? '' : ((d.title && d.title.trim()) || newTitle);

            console.log(
              '[artwork]',
              d.artwork_source || 'none',
              `stale=${stale}`,
              displayArtistRaw,
              displayTitleRaw
            );

            if (stale) {
              // Authoritative stale signal → wipe the player back to logo-only.
              setCurrentTrack({ ...DEFAULT_TRACK, artwork: DEFAULT_ARTWORK });
              return;
            }

            // The backend has already cleaned artist/title of slogans/CamelCase
            // — use those for the UI (player, Media Session, history, tab title).
            displayArtist = displayArtistRaw;
            displayTitle = displayTitleRaw;

            if (cover) {
              // STOP — admin-validated or itunes-scored, served centrally.
              const newTrack = {
                ...DEFAULT_TRACK,
                title: displayTitle,
                artist: displayArtist,
                artwork: cover,
                album: ''
              };
              setCurrentTrack(newTrack);
              try {
                localStorage.setItem(LAST_TRACK_STORAGE_KEY, JSON.stringify(newTrack));
              } catch (e) {
                console.warn('[AudioContext] localStorage save skipped:', e && e.message);
              }
              return;
            }

            // Level 1 responded but artwork === null. Keep the cleaned metadata
            // on screen, then fall through to the local cascade (Level 2).
            setCurrentTrack({
              ...DEFAULT_TRACK,
              title: displayTitle,
              artist: displayArtist
            });
          }

          // ---- LEVEL 2: existing local cascade (iTunes / Deezer / MusicBrainz) ----
          try {
            const itunesResponse = await axios.get(getApiUrl('enhanced-metadata'), {
              params: { artist: newArtist, title: newTitle }
            });

            if (itunesResponse.data.success && itunesResponse.data.artwork) {
              const newTrack = {
                ...DEFAULT_TRACK,
                title: displayTitle,
                artist: displayArtist,
                artwork: itunesResponse.data.artwork,
                album: itunesResponse.data.album || 'MAJESTIC JUKEBOX RADIO'
              };
              setCurrentTrack(newTrack);
              // Persist last good track so the next boot shows the cover instantly.
              try {
                localStorage.setItem(LAST_TRACK_STORAGE_KEY, JSON.stringify(newTrack));
              } catch (e) {
                console.warn('[AudioContext] localStorage save skipped:', e && e.message);
              }
            } else {
              // ---- LEVEL 3: no artwork anywhere → radio logo (DEFAULT_TRACK.artwork = null)
              setCurrentTrack({
                ...DEFAULT_TRACK,
                title: displayTitle,
                artist: displayArtist
              });
            }
          } catch (err) {
            console.error('Error fetching iTunes artwork:', err);
            // ---- LEVEL 3: network failure → radio logo
            setCurrentTrack({
              ...DEFAULT_TRACK,
              title: displayTitle,
              artist: displayArtist
            });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching metadata:', error);
      // Stream metadata fetch failed entirely → fallback to bare DEFAULT_TRACK
      // (avoids the previous song's pochette getting stuck on screen).
      setCurrentTrack(DEFAULT_TRACK);
      lastStreamTrackRef.current = '';
    }
  };

  // Update track info continuously (even when not playing) so artwork & title
  // are visible before the user presses Play.
  useEffect(() => {
    fetchMetadata();
    // Poll every 5 seconds (was 15s). Dedup above means zero extra network
    // load on /enhanced-metadata when the stream tag hasn't changed.
    metadataIntervalRef.current = setInterval(fetchMetadata, 5000);

    return () => {
      if (metadataIntervalRef.current) {
        clearInterval(metadataIntervalRef.current);
      }
    };
  }, []);

  // Handle play
  const play = async () => {
    try {
      setError(null);
      setIsLoading(true);
      wantPlayRef.current = true;
      retryCountRef.current = 0;
      clearReconnectTimer();

      if (audioRef.current) {
        // Always (re)open with a cache-busted URL to force a fresh stream
        audioRef.current.src = buildStreamUrl();
        console.log('Stream URL set:', audioRef.current.src);

        audioRef.current.load();

        await new Promise(resolve => setTimeout(resolve, 500));

        await audioRef.current.play();
        setIsPlaying(true);
        setIsLoading(false);

        // Initialize Media Session with default metadata
        updateMediaSession(currentTrack);

        // Kick off the watchdog so we auto-recover from drops
        startWatchdog();

        console.log('Audio playing');
      }
    } catch (err) {
      console.error('Error playing audio:', err);
      setError(`Connecting to stream... Please wait.`);
      setIsLoading(false);

      // Don't give up — let the watchdog retry in background
      if (wantPlayRef.current) {
        startWatchdog();
        reconnect('initial-play-failed');
      } else {
        setIsPlaying(false);
      }

      setTimeout(() => {
        setError(null);
      }, 2000);
    }
  };

  // Handle pause
  const pause = () => {
    // Mark user intent FIRST so the 'pause' event listener doesn't try to recover
    wantPlayRef.current = false;
    stopWatchdog();
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  // Handle volume change
  const changeVolume = (newVolume) => {
    const vol = Math.max(0, Math.min(1, newVolume));
    setVolume(vol);
    if (audioRef.current) {
      audioRef.current.volume = vol;
    }
  };

  const value = {
    isPlaying,
    volume,
    currentTrack,
    error,
    isLoading,
    play,
    pause,
    changeVolume,
    audioRef
  };

  return <AudioContext.Provider value={value}>{children}</AudioContext.Provider>;
};

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within AudioProvider');
  }
  return context;
};
